'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { getNotificationsAction } from '@/app/actions/get-notifications';
import { supabase } from '@/lib/supabase/client';
import type { Tables } from '@/types/supabase';

export type NotificationRow = Tables<'notifications'>;

type NotificationsContextType = {
  notifications: NotificationRow[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  browserPermission: NotificationPermission | null;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  requestBrowserNotifications: () => Promise<void>;
  refetch: () => void;
};

const NotificationsContext = createContext<NotificationsContextType | null>(null);

function tryShowBrowserNotification(row: NotificationRow) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  const userProbablyWatching =
    document.visibilityState === 'visible' && typeof document.hasFocus === 'function' && document.hasFocus();
  if (userProbablyWatching) return;

  try {
    new Notification(row.title, {
      body: row.body,
      tag: row.id,
    });
  } catch (e) {
    console.error('Error al mostrar notificación del navegador:', e);
  }
}

function triggerToastNotification(row: NotificationRow) {
  const type = row.type?.toLowerCase();

  if (type === 'security' || type === 'out_of_stock' || type === 'error') {
    toast.error(row.title, { description: row.body });
  } else if (type === 'warning' || type === 'stock_warning') {
    toast.warning(row.title, { description: row.body });
  } else if (type === 'order') {
    toast.success(row.title, { description: row.body });
  } else {
    toast.info(row.title, { description: row.body });
  }
}

type Props = {
  locationId?: string;
  children: ReactNode;
};

export function NotificationsProvider({ locationId, children }: Props) {
  const queryClient = useQueryClient();
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(() =>
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : null
  );

  const {
    data: notifications = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['notifications', locationId],
    queryFn: () => getNotificationsAction(locationId!),
    enabled: !!locationId,
  });

  const unreadCount = useMemo(() => notifications.filter(n => n.read_at === null).length, [notifications]);

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) {
        console.error('Error al marcar notificación como leída:', updateError);
        throw updateError;
      }
    },
    onSuccess: (_, id) => {
      queryClient.setQueryData<NotificationRow[]>(['notifications', locationId], prev =>
        (prev ?? []).map(n => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!locationId) return;
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('location_id', locationId)
        .is('read_at', null);

      if (updateError) {
        console.error('Error al marcar todas como leídas:', updateError);
        throw updateError;
      }
    },
    onSuccess: () => {
      const now = new Date().toISOString();
      queryClient.setQueryData<NotificationRow[]>(['notifications', locationId], prev =>
        (prev ?? []).map(n => ({ ...n, read_at: n.read_at ?? now }))
      );
    },
  });

  const requestBrowserNotifications = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      setBrowserPermission(result);
    } catch (e) {
      console.error('Error al solicitar permiso de notificaciones:', e);
    }
  }, []);

  useEffect(() => {
    if (!locationId) return;

    const channel = supabase
      .channel(`notifications:location_id=eq.${locationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `location_id=eq.${locationId}`,
        },
        payload => {
          const row = payload.new as NotificationRow;
          const now = new Date().toISOString();
          if (row.expires_at && row.expires_at <= now) return;

          queryClient.setQueryData<NotificationRow[]>(['notifications', locationId], prev => {
            const list = prev ?? [];
            if (list.some(n => n.id === row.id)) return list;
            return [row, ...list].slice(0, 50);
          });

          triggerToastNotification(row);
          tryShowBrowserNotification(row);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `location_id=eq.${locationId}`,
        },
        payload => {
          const row = payload.new as NotificationRow;
          queryClient.setQueryData<NotificationRow[]>(['notifications', locationId], prev => {
            const list = prev ?? [];
            return list.map(n => (n.id === row.id ? row : n));
          });
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' && err) {
          console.error('Canal de notificaciones en error:', err);
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [locationId, queryClient]);

  const markAsRead = useCallback(
    (id: string) => {
      markReadMutation.mutate(id);
    },
    [markReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        isError,
        browserPermission,
        markAsRead,
        markAllAsRead,
        requestBrowserNotifications,
        refetch,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de un NotificationsProvider');
  }
  return context;
}
